import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';

const StyledPaper = styled(Paper)(({ theme }) => ({
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    backgroundColor: '#ffffff',
}));

const StyledTableContainer = styled(TableContainer)({
    overflowX: 'auto',
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: 'rgba(0, 0, 0, 0.01)',
    },
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04) !important',
        transition: 'background-color 0.2s ease',
    },
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
}));

const HeaderCell = styled(TableCell)({
    fontWeight: 700,
    color: '#444',
    backgroundColor: '#f9fafb',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    padding: '16px',
});

const RefinedLeadCreditTable = () => {
    const [usersInfo, setUsersInfo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchLeadAdminStats = async (pageNum = 1) => {
        setLoading(true);
        try {
            const token =
                localStorage.getItem('authToken') ||
                localStorage.getItem('token') ||
                sessionStorage.getItem('authToken');

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`/api/v1/lead-credits/admin/analytics?page=${pageNum}&limit=10`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });
            const data = await res.json();

            if (data.success && data.data) {
                if (data.data.userList) setUsersInfo(data.data.userList);
                if (data.data.totalPages) setTotalPages(data.data.totalPages);
            }
        } catch (err) {
            console.error('Error fetching admin lead user stats:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeadAdminStats(page);
    }, [page]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
        <Box sx={{ width: '100%', p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 45, height: 45 }}>
                        <AccountBalanceWalletIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight="800" sx={{ color: '#1a202c', mb: 0.5 }}>
                            Builder Credit Analytics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Monitor user credit balances
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh Data">
                    <IconButton onClick={() => fetchLeadAdminStats(page)} color="primary" sx={{ border: '1px solid #e2e8f0' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <StyledPaper elevation={0}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography color="text.secondary" fontWeight="500">
                            Fetching latest records...
                        </Typography>
                    </Box>
                ) : (
                    <StyledTableContainer>
                        <Table stickyHeader size="medium">
                            <TableHead>
                                <TableRow>
                                    <HeaderCell>User Profile</HeaderCell>
                                    <HeaderCell>User ID</HeaderCell>
                                    <HeaderCell align="right">Total Credit</HeaderCell>
                                    <HeaderCell align="right">Available Credit</HeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usersInfo.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    usersInfo.map((record, idx) => (
                                        <StyledTableRow key={idx}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'rgba(25, 118, 210, 0.1)', color: 'primary.main' }}>
                                                        {(record.user?.username?.[0] || 'U').toUpperCase()}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="600">
                                                            {record.user?.username || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {record.user?.email || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                                    {record.user?.id || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="700" color="primary.main">
                                                    {record.total_credits.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="700" sx={{ color: '#059669' }}>
                                                    {(record.remaining_credits || 0).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
                {!loading && totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                )}
            </StyledPaper>
        </Box>
    );
};

export default RefinedLeadCreditTable;
